// TelcoTrack — Úřady: 3 kategorie (katalog 7 = seed discovery, ne povinnosti).
// Nic v Dotčených orgánech / Křížení sítí není REQUIRED. Overlay jen navrhne assess.

import {
  AUTHORITY_FILING_PACKAGE_KEYS,
  AUTHORITY_LAND_LIST_PACKAGE_KEYS,
  isAuthorityPovinnostKey,
  isAuthorityPovinnostLegacyKey,
  isCoreAuthorityPovinnost,
  type AuthorityAgendaKind,
  type AuthorityConstructionPrepKey,
  type AuthorityFilingPackageKey,
  type AuthorityNecessity,
} from './authority-domain.constants';

export const AUTHORITY_URADY_CATEGORIES = ['dotcene_organy', 'krizeni_siti', 'povinnosti'] as const;
export type AuthorityUradyCategory = (typeof AUTHORITY_URADY_CATEGORIES)[number];

export const AUTHORITY_URADY_IMPORT_KINDS = ['dotcene_organy', 'krizeni_siti'] as const;
export type AuthorityUradyImportKind = (typeof AUTHORITY_URADY_IMPORT_KINDS)[number];

export const AUTHORITY_URADY_CATEGORY_LABELS_CS: Record<AuthorityUradyCategory, string> = {
  dotcene_organy: 'Dotčené orgány',
  krizeni_siti: 'Křížení sítí',
  povinnosti: 'Povinnosti',
};

/**
 * Response follow-up window for Dotčené orgány after our last outbound message.
 * Not applied at import — only from send (ISDS/email) + this many calendar days.
 */
export const AUTHORITY_RESPONSE_DEADLINE_DAYS = 30;

/** Archaeology internal lead time — recommendation only, not a legal day count. */
export const AUTHORITY_ARCHAEOLOGY_LEAD_DAYS = 21;

export interface AuthorityDiscoveryCatalogEntry {
  subjectKey: string;
  labelCs: string;
  category: AuthorityUradyCategory;
  agendaKind: AuthorityAgendaKind;
  whenHintCs: string;
  specialHintCs: string;
  documentPackKeys: readonly AuthorityFilingPackageKey[];
  documentType: string;
  /** Overlay / catalog may only propose assess. */
  defaultNecessity: AuthorityNecessity;
  /**
   * false = never dump on catalog/apply (HZS, JES, KHS, PČR, MoD, …).
   * Overlay / explicit subjectKeys can still create an assess instance.
   */
  autoCreate: boolean;
  /** Show in map propose as unchecked suggestion even without a GIS hit (MoD). */
  suggestWithoutTrigger: boolean;
  /** Unknown DTM / unproven jurisdiction stays assess — never auto not_needed. */
  unknownStaysAssess: boolean;
  legalClaim: false;
}

const ORGAN_PACK: readonly AuthorityFilingPackageKey[] = [
  'c2_plan',
  'tz',
  'power_of_attorney',
  'cover_or_form',
  ...AUTHORITY_LAND_LIST_PACKAGE_KEYS,
  'subject_specific',
];

const NETWORK_PACK: readonly AuthorityFilingPackageKey[] = [
  'c2_plan',
  'tz',
  'cover_or_form',
  'subject_specific',
];

const PREP_PACK: readonly AuthorityFilingPackageKey[] = ['cover_or_form', 'subject_specific'];

function organ(
  subjectKey: string,
  labelCs: string,
  whenHintCs: string,
  specialHintCs: string,
  extras: Partial<Pick<AuthorityDiscoveryCatalogEntry, 'autoCreate' | 'suggestWithoutTrigger' | 'documentType'>> = {},
): AuthorityDiscoveryCatalogEntry {
  return {
    subjectKey,
    labelCs,
    category: 'dotcene_organy',
    agendaKind: 'official',
    whenHintCs,
    specialHintCs,
    documentPackKeys: ORGAN_PACK,
    documentType: extras.documentType ?? 'vyjadreni_doss',
    defaultNecessity: 'assess',
    autoCreate: extras.autoCreate ?? true,
    suggestWithoutTrigger: extras.suggestWithoutTrigger ?? false,
    unknownStaysAssess: true,
    legalClaim: false,
  };
}

function network(
  subjectKey: string,
  labelCs: string,
  whenHintCs: string,
  specialHintCs: string,
  extras: Partial<Pick<AuthorityDiscoveryCatalogEntry, 'autoCreate' | 'suggestWithoutTrigger'>> = {},
): AuthorityDiscoveryCatalogEntry {
  return {
    subjectKey,
    labelCs,
    category: 'krizeni_siti',
    agendaKind: 'network_crossing',
    whenHintCs,
    specialHintCs,
    documentPackKeys: NETWORK_PACK,
    documentType: 'vyjadreni_site',
    defaultNecessity: 'assess',
    autoCreate: extras.autoCreate ?? false,
    suggestWithoutTrigger: extras.suggestWithoutTrigger ?? false,
    unknownStaysAssess: true,
    legalClaim: false,
  };
}

/**
 * Catalog 7 seed — what can be discovered and why.
 * Not a mandatory checklist. CUSTOM rows are added by humans without redeploy.
 */
export const AUTHORITY_DISCOVERY_CATALOG: readonly AuthorityDiscoveryCatalogEntry[] = [
  // 7.1 Obec, SÚ, veřejní vlastníci
  organ(
    'obec',
    'Obec (veřejnoprávní orgán)',
    'Trasa / zařízení na území obce. Jedna instance na obec. Neplést s obcí jako vlastníkem cesty, pozemku, VO nebo kanalizace.',
    'Základní balíček stavby, parcely, termín, obnova povrchu.',
  ),
  organ(
    'stavebni_urad',
    'Stavební úřad',
    'Záměr potřebuje povolení nebo posouzení. Povolení je vlastní případ; právní moc je milník. Ne auto-povinné.',
    'Předepsaný formulář, doklady, vlastníci, DOSS, sítě.',
  ),
  organ(
    'spu',
    'SPÚ',
    'Parcela v jeho správě. Vyjádření k projektu ≠ smlouva ≠ předání pozemku.',
    'Lze evidovat posouzení § 104a ZEK; z toho neplyne souhlas se vstupem.',
  ),
  organ(
    'uzsvm',
    'ÚZSVM',
    'Parcela v jeho správě.',
    'Vyjádření k projektu ≠ smlouva ≠ předání pozemku.',
  ),
  organ(
    'jiny_verejny_vlastnik',
    'Jiný veřejný vlastník',
    'Parcela jiného veřejného vlastníka.',
    'Stejné oddělení vyjádření / smlouvy / vstupu jako u SPÚ.',
  ),

  // 7.3 Roads — SÚS + správní úřad are Dotčené orgány; utility owners stay Křížení sítí
  organ(
    'sus',
    'SÚS (správce komunikace)',
    'II./III. třída v trase. Krajská SÚS je Dotčený orgán, ne křížení sítí a ne povinnost.',
    'Okótovaná trasa, podvrt/překop, jámy, tabulka zásahu. Odděleně od silničního správního úřadu.',
    { documentType: 'sus' },
  ),
  organ(
    'silnicni_spravni_urad',
    'Silniční správní úřad',
    'Zvláštní užívání, DIO, částečná / úplná uzavírka. Odděleně od SÚS / ŘSD jako správce komunikace.',
    'Neslučovat se SÚS do jednoho checkboxu.',
  ),
  organ(
    'md',
    'Ministerstvo dopravy',
    'Dálnice — správní stránka vedle ŘSD a PČR.',
    'Jen při potvrzeném zásahu dálnice.',
    { autoCreate: false },
  ),
  organ(
    'kraj_ssu',
    'Kraj (silnice I. třídy)',
    'Správní úřad u silnice I. třídy vedle ŘSD a PČR.',
    'Jen overlay / potvrzený zásah.',
    { autoCreate: false },
  ),
  organ(
    'obecni_urad_komunikace',
    'Obecní úřad (místní komunikace)',
    'Správní úřad u místní komunikace. Odděleně od obce jako veřejnoprávního orgánu i od obce jako správce VO.',
    'Ne slučovat do jednoho checkboxu obce.',
  ),
  organ(
    'pcr',
    'PČR',
    'Omezení provozu, DIO, uzavírka, nebo výzva jako DOSS. Ne univerzálně.',
    'Nevymýšlej pravidla mimo potvrzený zásah.',
    { autoCreate: false },
  ),
  organ(
    'dopravni_urad',
    'Dopravní úřad',
    'Uzavírka zasáhne linku / zastávku / jízdní řád. Jen skutečně dotčení dopravci, ne všichni v kraji.',
    'Objížďka = i vlastníci objízdných komunikací.',
    { autoCreate: false },
  ),

  // 7.4 Railway — authority
  organ(
    'drazni_urad',
    'Drážní úřad',
    'Úkon to vyžaduje (křížení, stavba dráhy, ochranné pásmo). Infrastruktura je zvlášť v Křížení sítí.',
    'Nevysypávat bez zásahu.',
    { autoCreate: false },
  ),

  // 7.5 Water — authority / Povodí
  organ(
    'povodi',
    'Povodí / správce toku',
    'Křížení nebo souběh s tokem, břeh / koryto / most / VD, záplava, OP vodního zdroje, zařízení Povodí.',
    'Řez křížení, HDD/výkop, start/cíl jámy, odstupy od břehu, hloubka pod dnem, obnova břehu, havárie.',
  ),
  organ(
    'vodopravni',
    'Vodoprávní úřad',
    'Vodoprávní úkon nebo JES k vodnímu zákonu. Ne auto JES na každou stavbu.',
    'Závislost na vyjádření Povodí jen když ho konkrétní orgán vyžaduje.',
    { autoCreate: false },
  ),

  // 7.6 Environment / JES — never auto-create JES
  organ(
    'jes',
    'JES',
    'Navrhnout jen když povolený záměr potřebuje integrovaný environmentální úkon. Nikdy auto-povinné.',
    'Řešitel podle úkonu/území: ORP, kraj, MŽP, AOPK, správa NP. Závislost na Povodí/Lesy/AOPK jen konkrétní.',
    { autoCreate: false },
  ),
  organ(
    'koordinovane_stanovisko',
    'Závazné koordinované stanovisko',
    'Náhrada JES v některých městech. Varianta k posouzení, ne auto dvě žádosti.',
    'Ne auto rušení JES.',
    { autoCreate: false },
  ),
  organ(
    'aopk',
    'AOPK',
    'CHÚ, NP, CHKO, ptačí, Natura — použitelnost k potvrzení.',
    '',
  ),

  // 7.7 Forest / ZPF
  organ(
    'lesy_cr',
    'Lesy ČR',
    'Jejich pozemek nebo zařízení / PUPFL / OP lesa.',
    'Zábor; v OP kóta k hranici lesa.',
  ),
  organ(
    'organ_lesu',
    'Státní správa lesů',
    'Správní úkon k lesu. Odděleně od vlastníka/správce lesa a od Lesy ČR.',
    'Typické podklady dle specifikace les / ZPF.',
    { autoCreate: false },
  ),
  organ(
    'organ_zpf',
    'Orgán ochrany ZPF',
    'Správní úkon k ZPF je potřeba.',
    'Ne slučovat s vlastníkem parcely.',
    { autoCreate: false },
  ),

  // 7.8 Heritage / archaeology
  organ(
    'pamatkova_pece',
    'Orgán památkové péče',
    'KP, rezervace/zóna, OP, historický prvek k ručnímu posouzení. Odděleně od NPÚ a od vlastníka památky.',
    '',
    { autoCreate: false },
  ),
  organ(
    'npu',
    'NPÚ',
    'PZ, historické centrum, KP — odborná organizace, ne vždy totožná s orgánem památkové péče.',
    'Podrobnější situace po konzultaci; kaplička/socha = oslovit PP.',
  ),
  organ(
    'archeologicky_ustav',
    'Archeologický ústav',
    'Oznamovací povinnost v přípravě na území s archeologickými nálezy. Sledovat oznámení, dohodu, termín zemních prací, dohled, závěr.',
    `Zákon nemá univerzální lhůtu; interní předstih cca ${AUTHORITY_ARCHAEOLOGY_LEAD_DAYS} dní je jen doporučení.`,
  ),

  // 7.9 Special — never universal
  organ(
    'mo_sem',
    'MO – SEM',
    'Interní praxe bez konkrétního triggeru — jen návrh k prověření. Pravidlo lze později změnit v katalogu.',
    '',
    { autoCreate: false, suggestWithoutTrigger: true },
  ),
  organ(
    'hzs',
    'HZS',
    'Není univerzální z FTTH šablony. Jen potvrzený trigger, výzva orgánu, nebo ruční přidání.',
    'Nevymýšlej pravidla.',
    { autoCreate: false, documentType: 'stanovisko_hzs' },
  ),
  organ(
    'khs',
    'KHS',
    'Jen potvrzený trigger, výzva, nebo ruční přidání.',
    'Nevymýšlej pravidla.',
    { autoCreate: false },
  ),
  organ(
    'bansky_urad',
    'Báňský úřad / OBU',
    'Průnik s CHLÚ nebo výzva. Overlay ČGS, potvrzení člověkem.',
    '',
    { autoCreate: false },
  ),
  organ(
    'cgs_chlu',
    'CHLÚ / OBU (ČGS)',
    'Průnik s chráněným ložiskovým územím — podnět k OBU.',
    'Jen overlay; ne slepý katalog.',
    { autoCreate: false },
  ),
  organ(
    'ucl',
    'ÚCL / letiště',
    'Jen potvrzený zásah ochranného pásma letiště, výzva, nebo ruční přidání.',
    '',
    { autoCreate: false },
  ),
  organ(
    'plavebni_sprava',
    'Plavební správa',
    'Jen potvrzený zásah vodní cesty, výzva, nebo ruční přidání.',
    '',
    { autoCreate: false },
  ),
  organ(
    'custom_organ',
    'Vlastní orgán',
    'Ručně přidaný orgán bez změny kódu.',
    'Každá instance má vlastní podklady a vzor.',
    { autoCreate: false },
  ),

  // 7.2 + 7.3/7.4 infrastructure side — Křížení sítí
  network(
    'elektronicke_komunikace',
    'Elektronické komunikace',
    'Síť v trase, OP, křížení, souběh, napojení, nebo nelze vyloučit zásah.',
    'Existence ≠ vyjádření k projektu, pokud je síť dotčena.',
  ),
  network(
    'elektrina',
    'Elektřina',
    'Vedení v trase / OP / křížení / souběh, nebo nelze vyloučit zásah.',
    'Existence ≠ vyjádření k projektu.',
  ),
  network(
    'plyn',
    'Plyn',
    'Plynovod v trase / BP / křížení / souběh, nebo nelze vyloučit zásah.',
    'Existence ≠ vyjádření k projektu.',
  ),
  network(
    'vodovod',
    'Vodovod',
    'Vodovod (ne Povodí) v trase / OP / křížení, nebo nelze vyloučit zásah.',
    'Odděleně od Povodí / správce toku.',
  ),
  network(
    'kanalizace',
    'Kanalizace',
    'Kanalizace v trase. Neznámá DTM obecní kanalizace = nutno posoudit, nikdy auto nepotřebné ani povinné.',
    'Obec jako provozovatel kanalizace ≠ obec jako úřad.',
  ),
  network(
    'teplo',
    'Teplo',
    'Rozvod tepla v trase / OP / křížení, nebo nelze vyloučit zásah.',
    'Existence ≠ vyjádření k projektu.',
  ),
  network(
    'verejne_osvetleni',
    'Veřejné osvětlení',
    'VO v trase. Obec jako provozovatel VO zvlášť od obce-úřadu.',
    '',
  ),
  network(
    'telematika',
    'Telematika (silnice / dráha)',
    'Silniční nebo drážní telematika v trase.',
    '',
  ),
  network(
    'produktovody',
    'Produktovody',
    'Produktovod v trase / BP / křížení.',
    '',
  ),
  network(
    'zavlahy',
    'Závlahy',
    'Závlahové zařízení v trase.',
    '',
  ),
  network(
    'obecni_site',
    'Obecní technická infrastruktura',
    'Možná/potvrzená obecní síť VO / kanalizace / rozhlas / telematika. Není-li v DTM → nutno posoudit, ne nepotřebné.',
    'Vlastník / provozovatel, ne obec jako úřad.',
  ),
  network(
    'rsd',
    'ŘSD (správce komunikace)',
    'Dálnice / I. třída v trase. Infrastruktura, ne správní úřad.',
    'Technické vyjádření správce; majetkoprávní k pozemku komunikace je zvlášť v Povinnostech.',
  ),
  network(
    'spravce_komunikace',
    'Správce komunikace',
    'Místní / účelová komunikace — skutečný správce. Neprokázaná příslušnost = nutno posoudit.',
    'Odděleně od silničního správního úřadu a od PČR.',
  ),
  network(
    'zeleznicni_infrastruktura',
    'Železniční infrastruktura (SŽ / jiný)',
    'Křížení, pozemek dráhy, stavba dráhy, OP. Vlastník infrastruktury.',
    'Podélné/příčné řezy, technologie křížení, vzdálenosti, harmonogram, bezpečnost, výluky.',
  ),
  network(
    'zeleznicni_provozovatel',
    'Provozovatel dráhy',
    'Jen když je potvrzen zásah do provozu / přístupu.',
    'Ne slučovat s Drážním úřadem ani s vlastníkem infrastruktury.',
  ),
  network(
    'zeleznicni_telekom_energie',
    'Drážní telekom / energie',
    'Vlastníci drážních sítí při křížení / OP.',
    '',
  ),
  network(
    'custom_sit',
    'Ručně přidaná síť',
    'Vlastník / provozovatel mimo katalog, bez redeploye.',
    'Každá instance má vlastní podklady a vzor.',
  ),
] as const;

const DISCOVERY_BY_KEY = new Map(AUTHORITY_DISCOVERY_CATALOG.map((row) => [row.subjectKey, row]));

/** Majetkoprávní / parcel track — belongs under Povinnosti, not an orgán checkbox. */
export const AUTHORITY_PROPERTY_TRACK_TYPES = ['souhlas_vlastnika', 'vecne_bremeno', 'vypis_kn'] as const;
export type AuthorityPropertyTrackType = (typeof AUTHORITY_PROPERTY_TRACK_TYPES)[number];

export const AUTHORITY_PROPERTY_TRACK_LABELS_CS: Record<AuthorityPropertyTrackType, string> = {
  souhlas_vlastnika: 'Souhlas vlastníka',
  vecne_bremeno: 'Věcné břemeno',
  vypis_kn: 'Výpis KN',
};

export function isAuthorityPropertyTrackType(value: string | null | undefined): boolean {
  return (AUTHORITY_PROPERTY_TRACK_TYPES as readonly string[]).includes((value ?? '').trim());
}

export function discoveryEntry(subjectKey: string | null | undefined): AuthorityDiscoveryCatalogEntry | undefined {
  return DISCOVERY_BY_KEY.get((subjectKey ?? '').trim());
}

export function isAuthorityUradyCategory(value: string | null | undefined): value is AuthorityUradyCategory {
  return (AUTHORITY_URADY_CATEGORIES as readonly string[]).includes((value ?? '').trim());
}

export function agendaKindForSubject(subjectKey: string | null | undefined): AuthorityAgendaKind {
  return discoveryEntry(subjectKey)?.agendaKind ?? 'official';
}

export function documentTypeForSubject(subjectKey: string | null | undefined): string {
  const entry = discoveryEntry(subjectKey);
  if (!entry) return 'vyjadreni_doss';
  if (entry.documentType === 'stanovisko_hzs') return 'stanovisko_hzs';
  if (entry.category === 'krizeni_siti') return entry.subjectKey;
  return entry.documentType;
}

export function documentPackForSubject(subjectKey: string | null | undefined): readonly AuthorityFilingPackageKey[] {
  const pack = discoveryEntry(subjectKey)?.documentPackKeys;
  if (pack?.length) return pack;
  if (isAuthorityPropertyTrackType(subjectKey)) return PREP_PACK;
  if (isAuthorityPovinnostKey(subjectKey)) {
    return PREP_PACK;
  }
  return AUTHORITY_FILING_PACKAGE_KEYS;
}

/** Vytyčení konkrétní sítě — není checklistová položka `stakeout` / `prep:stakeout`. */
export const NETWORK_STAKEOUT_DOCUMENT_TYPE = 'network_stakeout';
export const NETWORK_STAKEOUT_LABEL_CS = 'Vytyčení sítě';
/** Polygon-matrix suffix so obeznámení (`elektrina`) and vytyčení (`elektrina:vytyceni`) stay distinct. */
export const NETWORK_STAKEOUT_MATRIX_SUFFIX = ':vytyceni';

export function catalogKeyForNetwork(subjectKey: string, ownerKey?: string | null): string {
  const owner = (ownerKey ?? '').trim();
  return owner ? `net:${subjectKey}:${owner}` : `net:${subjectKey}`;
}

/** `net:{subject}:{owner}:vytyceni`, or `net:{subject}:vytyceni` when the crossing has no owner. */
export function catalogKeyForNetworkStakeout(subjectKey: string, ownerKey?: string | null): string {
  const subject = (subjectKey ?? '').trim();
  const owner = (ownerKey ?? '').trim();
  return owner ? `net:${subject}:${owner}:vytyceni` : `net:${subject}:vytyceni`;
}

/** Owner segment of `net:{subject}:{owner}` (empty when the key has no owner). */
export function networkCatalogOwnerKey(catalogKey: string | null | undefined, subjectKey: string): string {
  const key = (catalogKey ?? '').trim();
  const subject = (subjectKey ?? '').trim();
  const prefix = `net:${subject}:`;
  if (!subject || !key.startsWith(prefix)) return '';
  const rest = key.slice(prefix.length);
  if (!rest || rest === 'vytyceni') return '';
  return rest.endsWith(':vytyceni') ? rest.slice(0, -':vytyceni'.length) : rest;
}

export function networkStakeoutObligationTitle(networkTitle: string): string {
  const name = networkTitle.trim() || 'síť';
  return `Vytyčení — ${name}`;
}

export function isNetworkStakeoutDocument(input: {
  documentType?: string | null;
  catalogKey?: string | null;
}): boolean {
  if ((input.documentType ?? '').trim() === NETWORK_STAKEOUT_DOCUMENT_TYPE) return true;
  return (input.catalogKey ?? '').trim().endsWith(':vytyceni');
}

/** Checklist Povinnosti row `stakeout` / `prep:stakeout` — not a per-network sibling. */
export function isConstructionPrepStakeout(input: {
  documentType?: string | null;
  subjectKey?: string | null;
  catalogKey?: string | null;
}): boolean {
  const documentType = (input.documentType ?? '').trim();
  const subject = (input.subjectKey ?? '').trim();
  const catalog = (input.catalogKey ?? '').trim();
  return documentType === 'stakeout' || subject === 'stakeout' || catalog === 'prep:stakeout';
}

/**
 * Hub matrix / documentStatuses key that belongs on Vytyčení, not Povinnosti
 * (`stakeout`, `prep:stakeout`, `network_stakeout`, `*:vytyceni`).
 */
export function isAuthorityVytyceniListKey(key: string | null | undefined): boolean {
  const k = (key ?? '').trim();
  if (!k) return false;
  if (k === 'stakeout' || k === 'prep:stakeout') return true;
  return isNetworkStakeoutMatrixKey(k);
}

/** Rows listed on the Vytyčení card / hub — never on the Povinnosti table. */
export function isAuthorityVytyceniObligation(input: {
  documentType?: string | null;
  subjectKey?: string | null;
  catalogKey?: string | null;
}): boolean {
  if (isNetworkStakeoutDocument(input) || isConstructionPrepStakeout(input)) return true;
  return isNetworkStakeoutMatrixKey(input.documentType) || isNetworkStakeoutMatrixKey(input.subjectKey);
}

export function isNetworkStakeoutMatrixKey(key: string | null | undefined): boolean {
  const k = (key ?? '').trim();
  if (!k) return false;
  if (k === NETWORK_STAKEOUT_DOCUMENT_TYPE) return true;
  return k.endsWith(NETWORK_STAKEOUT_MATRIX_SUFFIX);
}

export function networkSubjectFromStakeoutMatrixKey(key: string | null | undefined): string {
  const k = (key ?? '').trim();
  if (!k || k === NETWORK_STAKEOUT_DOCUMENT_TYPE) return '';
  if (k.endsWith(NETWORK_STAKEOUT_MATRIX_SUFFIX)) {
    return k.slice(0, -NETWORK_STAKEOUT_MATRIX_SUFFIX.length);
  }
  return k;
}

/** Stavba / Případy matrix key (subject first; stakeouts get `:vytyceni`). */
export function authorityObligationMatrixKey(input: {
  documentType?: string | null;
  subjectKey?: string | null;
  catalogKey?: string | null;
}): string | null {
  const documentType = (input.documentType ?? '').trim();
  const subject = (input.subjectKey ?? '').trim();
  if (isNetworkStakeoutDocument(input) || isNetworkStakeoutMatrixKey(documentType) || isNetworkStakeoutMatrixKey(subject)) {
    const base = subject && !isNetworkStakeoutMatrixKey(subject) ? subject : networkSubjectFromStakeoutMatrixKey(subject) || 'sit';
    return `${base}${NETWORK_STAKEOUT_MATRIX_SUFFIX}`;
  }
  return subject || documentType || null;
}

export type AuthorityOutboundTopic = 'network_statement' | 'network_stakeout' | 'other';

export function authorityOutboundTopic(input: {
  agendaKind?: string | null;
  subjectKey?: string | null;
  documentType?: string | null;
  catalogKey?: string | null;
}): AuthorityOutboundTopic {
  if (isNetworkStakeoutDocument(input) || isNetworkStakeoutMatrixKey(input.documentType)) {
    return 'network_stakeout';
  }
  if (isNetworkCrossingStatement(input)) return 'network_statement';
  return 'other';
}

/** Channels this topic may use (before role filter). */
export function allowedChannelsForAuthorityOutbound(
  topic: AuthorityOutboundTopic,
): readonly ('email' | 'isds' | 'portal')[] {
  if (topic === 'network_statement') return ['portal'];
  if (topic === 'network_stakeout') return ['email', 'isds'];
  return ['email', 'isds', 'portal'];
}

/** Křížení sítí statement row — not the sibling vytyčení povinnost. */
export function isNetworkCrossingStatement(input: {
  agendaKind?: string | null;
  subjectKey?: string | null;
  documentType?: string | null;
  catalogKey?: string | null;
}): boolean {
  return uradyCategoryForObligation(input) === 'krizeni_siti' && !isNetworkStakeoutDocument(input);
}

export type NetworkCrossingPairable = {
  id: string;
  documentType?: string | null;
  catalogKey?: string | null;
  subjectKey?: string | null;
  parentObligationId?: string | null;
  agendaKind?: string | null;
};

/** Expected `net:…:vytyceni` key for a crossing statement. Unique per custom row via obligation id. */
export function catalogKeyForCrossingStakeoutSibling(crossing: {
  id?: string | null;
  subjectKey?: string | null;
  documentType?: string | null;
  catalogKey?: string | null;
}): string {
  const subject = (crossing.subjectKey ?? crossing.documentType ?? 'sit').trim() || 'sit';
  const owner = networkCatalogOwnerKey(crossing.catalogKey, subject);
  if (owner) return catalogKeyForNetworkStakeout(subject, owner);
  if ((crossing.catalogKey ?? '').trim()) return catalogKeyForNetworkStakeout(subject);
  return catalogKeyForNetworkStakeout(subject, (crossing.id ?? '').trim() || null);
}

/** Pair one crossing statement with its `network_stakeout` sibling (parent id, then catalog key). */
export function findNetworkStakeoutSibling<T extends NetworkCrossingPairable>(
  crossing: T,
  obligations: readonly T[],
): T | undefined {
  if (!crossing?.id || isNetworkStakeoutDocument(crossing)) return undefined;
  const byParent = obligations.find(
    (row) => isNetworkStakeoutDocument(row) && (row.parentObligationId ?? '').trim() === crossing.id,
  );
  if (byParent) return byParent;
  const expected = catalogKeyForCrossingStakeoutSibling(crossing);
  const byKey = obligations.find((row) => (row.catalogKey ?? '').trim() === expected);
  if (byKey) return byKey;
  const subject = (crossing.subjectKey ?? '').trim();
  if (!subject) return undefined;
  const sameSubject = obligations.filter(
    (row) => isNetworkStakeoutDocument(row) && (row.subjectKey ?? '').trim() === subject,
  );
  return sameSubject.length === 1 ? sameSubject[0] : undefined;
}

export function uradyCategoryForObligation(input: {
  agendaKind?: string | null;
  subjectKey?: string | null;
  documentType?: string | null;
}): AuthorityUradyCategory {
  const agenda = (input.agendaKind ?? '').trim();
  const subject = (input.subjectKey ?? '').trim();
  const documentType = (input.documentType ?? '').trim();
  if (isCoreAuthorityPovinnost({ subjectKey: subject, documentType })) {
    return 'povinnosti';
  }
  // Listed under hub tab Vytyčení — not Křížení sítí and not checklist Povinnosti.
  if (
    documentType === NETWORK_STAKEOUT_DOCUMENT_TYPE ||
    isNetworkStakeoutMatrixKey(documentType) ||
    isNetworkStakeoutMatrixKey(subject)
  ) {
    return 'povinnosti';
  }
  if (
    agenda === 'construction_prep' &&
    !isAuthorityPovinnostLegacyKey(subject) &&
    !isAuthorityPovinnostLegacyKey(documentType) &&
    !isAuthorityPropertyTrackType(subject) &&
    !isAuthorityPropertyTrackType(documentType)
  ) {
    return 'povinnosti';
  }
  if (agenda === 'network_crossing') return 'krizeni_siti';
  const discovered = discoveryEntry(subject);
  if (discovered) return discovered.category;
  if (agenda === 'official') return 'dotcene_organy';
  return 'dotcene_organy';
}

export function isResponseTrackedCategory(category: AuthorityUradyCategory): boolean {
  return category === 'dotcene_organy' || category === 'krizeni_siti';
}

/** Overlay / DTM may never confirm needed or not_needed. */
export function discoveryProposalNecessity(_subjectKey?: string | null): AuthorityNecessity {
  return 'assess';
}

export function catalogDiscoveryToApply(subjectKeys?: string[] | null): AuthorityDiscoveryCatalogEntry[] {
  if (!subjectKeys?.length) return [];
  return AUTHORITY_DISCOVERY_CATALOG.filter(
    (row) =>
      subjectKeys.includes(row.subjectKey) &&
      row.subjectKey !== 'custom_organ' &&
      row.subjectKey !== 'custom_sit',
  );
}

export function isAuthorityUradyImportKind(value: string | null | undefined): value is AuthorityUradyImportKind {
  return (AUTHORITY_URADY_IMPORT_KINDS as readonly string[]).includes((value ?? '').trim());
}

export function proposalMatchesImportKind(
  proposal: { agendaKind?: string | null; subjectKey?: string | null; documentType?: string | null },
  importKind: AuthorityUradyImportKind,
): boolean {
  if (isImportablePovinnostProposal(proposal)) return false;
  return uradyCategoryForObligation(proposal) === importKind;
}

/** Overlay / catalog import never creates Povinnosti. */
export function isImportablePovinnostProposal(proposal: {
  agendaKind?: string | null;
  subjectKey?: string | null;
  documentType?: string | null;
}): boolean {
  return (
    isAuthorityPovinnostKey(proposal.subjectKey) ||
    isAuthorityPovinnostKey(proposal.documentType) ||
    uradyCategoryForObligation(proposal) === 'povinnosti'
  );
}

export function dtmReviewNetworkKeys(): readonly string[] {
  return [
    'elektronicke_komunikace',
    'elektrina',
    'plyn',
    'vodovod',
    'kanalizace',
    'obecni_site',
  ] as const;
}

export function overlayNetworkCompanion(organSubjectKey: string): string | null {
  if (organSubjectKey === 'drazni_urad') return 'zeleznicni_infrastruktura';
  return null;
}

export function prepDocumentPack(_key: AuthorityConstructionPrepKey): readonly AuthorityFilingPackageKey[] {
  return PREP_PACK;
}
